import type { PublicHostedAuthSession, StartProviderAuthIntentBody } from '@unipile/provider-shared';
import type { ProviderAuthenticationIntentResponse } from '@unipile/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocationConfig } from '../../../hooks/useLocationConfig';
import { axiosInstance } from '../../../libs/axios';
import type { MutationConfig } from '../../../types/react-query-utility';

/**
 * This hook must live in UI package because it's used in provider packages.
 */

export const startProviderAuthIntent = async (
  { data }: { data: StartProviderAuthIntentBody },
  { signal }: { signal?: AbortSignal } = {},
): Promise<ProviderAuthenticationIntentResponse> => {
  const response = await axiosInstance.post<ProviderAuthenticationIntentResponse>('/v2/auth/intent', data, { signal });

  return response.data;
};

type UseStartProviderAuthIntentOptions = {
  mutationConfig?: MutationConfig<typeof startProviderAuthIntent>;
  getSignal?: () => AbortSignal | undefined;
};

export const useStartProviderAuthIntent = ({ mutationConfig, getSignal }: UseStartProviderAuthIntentOptions = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};
  const { getLocationConfig } = useLocationConfig();

  const q = useQueryClient();

  return useMutation({
    onSuccess: (...args) => {
      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: ({ data }: { data: StartProviderAuthIntentBody }): Promise<ProviderAuthenticationIntentResponse> => {
      // Result of useHostedAuthPayload should be in react-query's cache as it's the first query made when loading the page
      const session = q.getQueryData<PublicHostedAuthSession>(['session']);

      if (!session) throw new Error('Session not in cache');

      const provider = data.provider;

      if (provider !== 'mock') {
        // Forward all provider-specific config that was given during the creation of the hosted auth link (proxy, oauth scope, linkedin/facebook products...)
        // Providers with products show the product selection screen by default in Hosted Auth
        data.config =
          provider === 'linkedin' || provider === 'facebook'
            ? {
                ...session.config?.[provider],
                allow_product_selection: session.config?.[provider]?.allow_product_selection ?? true,
              }
            : session.config?.[provider];

        // If the user has defined a proxy in the context, override
        const locationConfig = getLocationConfig(data.provider);
        if (locationConfig.custom_proxy) data.config = { ...data.config, custom_proxy: locationConfig.custom_proxy };
        if (locationConfig.auto_proxy_config?.country) {
          if (data.config && 'auto_proxy_config' in data.config) {
            data.config.auto_proxy_config = {
              ...data.config.auto_proxy_config,
              ip: undefined,
              country: locationConfig.auto_proxy_config.country,
            };
          } else {
            data.config = { ...data.config, auto_proxy_config: locationConfig.auto_proxy_config };
          }
        }
      }

      /**
       * This is just to detect if the provider uses oauth flow,
       * but the actual redirect_uri is just an empty string in input and actually defined in the following block
       */
      if ('oauth_callback_redirect_uri' in data) {
        // Set the actual redirect_uri from the hosted auth payload
        data.oauth_callback_redirect_uri = session.redirect_uri;

        // Forward the custom oauth scope from the config given in hosted auth payload
        const scope_override = session?.config?.[data.provider]?.oauth_scope;
        if (scope_override) data.config = { ...data.config, oauth_scope: scope_override };

        // Forward login hint for OAuth
        if (session.config?.global?.login_hint) data.login_hint = session.config.global.login_hint;
      }

      // Set the state from the hosted auth payload
      if (session.state) {
        data.state = session.state;
      }

      if (session.account_id) {
        data.account_id = session.account_id;
      }

      return startProviderAuthIntent({ data }, { signal: getSignal?.() });
    },
  });
};
